import React, { useRef, useState } from 'react';
import EnrollmentQrModal from './EnrollmentQrModal';
import { requestEnrollmentToken } from '../../helper/NX5/NX5_EnrollmentTokenHelper';
import { EnrollmentTokenError } from '../../helper/NX5/enrollmentToken';

const emptyDialog = (machineUdi, extra = {}) => ({
  machineUdi,
  enrollmentToken: '',
  expiresAt: '',
  error: '',
  busy: false,
  certificateIssued: false,
  ...extra,
});

const UnregisteredMachinesTable = ({ devices, canIssue }) => {
  const [dialog, setDialog] = useState(null);
  const [blockedUdis, setBlockedUdis] = useState(() => new Set());
  const requestIdRef = useRef(0);

  const generate = (machineUdi) => {
    if (!canIssue || blockedUdis.has(machineUdi)) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setDialog(emptyDialog(machineUdi, { busy: true }));

    requestEnrollmentToken(machineUdi)
      .then((result) => {
        if (requestIdRef.current !== requestId) return;
        setDialog(emptyDialog(result.machine_udi || machineUdi, {
          enrollmentToken: result.enrollment_token,
          expiresAt: result.expires_at,
        }));
      })
      .catch((err) => {
        if (requestIdRef.current !== requestId) return;
        const status = err instanceof EnrollmentTokenError ? err.status : err?.status;
        const certificateIssued = status === 409;
        if (certificateIssued) {
          setBlockedUdis((current) => {
            const next = new Set(current);
            next.add(machineUdi);
            return next;
          });
        }
        setDialog(emptyDialog(machineUdi, {
          error: err?.message || 'Could not generate an enrollment code.',
          certificateIssued,
        }));
      });
  };

  const closeDialog = () => {
    requestIdRef.current += 1;
    setDialog(null);
  };

  return (
    <div className="fleet-unreg">
      <p className="fleet-unreg-note">
        Patient monitors whose UDI starts with P0, with permanent and temporary registration both off.
      </p>
      {!canIssue && (
        <p className="fleet-unreg-note">
          Only an admin or production user can generate an enrollment code.
        </p>
      )}
      {devices.length === 0 ? (
        <div className="fleet-empty">No unregistered machines.</div>
      ) : (
        <div className="fleet-unreg-table-wrap">
          <table className="fleet-unreg-table">
            <thead>
              <tr>
                <th scope="col">Machine UDI</th>
                <th scope="col" className="fleet-unreg-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => {
                const issued = blockedUdis.has(device.machineUDI);
                return (
                  <tr key={device.machineUDI}>
                    <td className="fleet-unreg-udi">{device.machineUDI}</td>
                    <td className="fleet-unreg-action">
                      <button
                        type="button"
                        className="fleet-token-btn"
                        disabled={!canIssue || issued || (dialog?.busy && dialog.machineUdi === device.machineUDI)}
                        onClick={() => generate(device.machineUDI)}
                      >
                        {issued ? 'Certificate issued' : 'Generate token QR'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dialog && (
        <EnrollmentQrModal
          machineUdi={dialog.machineUdi}
          enrollmentToken={dialog.enrollmentToken}
          expiresAt={dialog.expiresAt}
          error={dialog.error}
          busy={dialog.busy}
          certificateIssued={dialog.certificateIssued}
          onGenerateAgain={() => generate(dialog.machineUdi)}
          onClose={closeDialog}
        />
      )}
    </div>
  );
};

export default UnregisteredMachinesTable;
